import React from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  CheckSquare,
  RotateCcw,
  RotateCw,
  X,
  Download,
  XCircle,
  ChevronUp,
  ChevronDown,
  SearchIcon,
  Edit
} from 'lucide-react';
import Select from 'react-select';

const ActionButtons = ({
  isButtonContainerExpanded,
  toggleButtonContainer,
  activeCell,
  selectionStart,
  selectionEnd,
  editingCell,
  onToggleAutomaticEntry,
  onToggleRequirementsForm,
  currentSheetType,
  handleFetchPresetRequirements,
  undo,
  redo,
  deleteSelectedRows,
  downloadAllSpreadsheets,
  clearAllFilters,
  hasActiveFilters,
  globalSearch,
  setGlobalSearch,
  selectedColumns,
  setSelectedColumns,
  visibleColumnNames,
  columnSelectRef,
  isCustomerView
}) => {
  const buttonConfigs = [
    ...(onToggleRequirementsForm && currentSheetType === "Requirements Checklist" ? [{
      onClick: handleFetchPresetRequirements,
      icon: CheckSquare,
      text: 'Preset Requirements',
      primary: true,
      gradient: 'from-indigo-500 via-indigo-600 to-indigo-700'
    }] : []),
    ...(!isCustomerView ? [
      { 
        onClick: undo, 
        icon: RotateCcw, 
        text: 'Undo',
        shortcut: '⌘Z'
      },
      { 
        onClick: redo, 
        icon: RotateCw, 
        text: 'Redo',
        shortcut: '⌘⇧Z'
      }
    ] : []),
    ...(!isCustomerView ? [{
      onClick: deleteSelectedRows,
      icon: X,
      text: 'Delete Row',
      primary: selectionStart && selectionEnd && (selectionStart[0] !== null && selectionEnd[0] !== null),
      disabled: !selectionStart || !selectionEnd || selectionStart[0] === null || selectionEnd[0] === null,
      gradient: 'from-red-500 via-red-600 to-rose-600',
      shortcut: 'Del'
    }] : []),
    {
      onClick: downloadAllSpreadsheets,
      icon: Download,
      text: 'Download',
      shortcut: '⌘D'
    },
    {
      onClick: clearAllFilters,
      icon: XCircle,
      text: 'Clear Filters',
      primary: hasActiveFilters || globalSearch,
      disabled: !hasActiveFilters && !globalSearch,
      gradient: 'from-orange-500 via-red-500 to-pink-500'
    },
  ];

  return (
    <>
      {/* Top Row: Show/Hide Controls Button and Position Indicators */}
      <div className="px-4 py-3 border-b border-white/20">
        <div className="grid grid-cols-3 gap-3">
          {/* Show/Hide Controls Button */}
          <button
            onClick={toggleButtonContainer}
            className="group flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl
              font-medium text-sm
              bg-gradient-to-r from-emerald-500 via-emerald-500 to-emerald-400
              text-white shadow-lg hover:shadow-xl hover:brightness-110
              transition-all duration-200
              w-full"
            aria-label={
              isButtonContainerExpanded
                ? 'Collapse controls'
                : 'Expand controls'
            }
          >
            <div className="transition-transform duration-200">
              {isButtonContainerExpanded ? (
                <ChevronUp size={16} className="group-hover:-translate-y-0.5" />
              ) : (
                <ChevronDown size={16} className="group-hover:translate-y-0.5" />
              )}
            </div>
            <span>
              {isButtonContainerExpanded ? 'Hide' : 'Show'} Controls
            </span>
            <span className="text-xs text-emerald-100 bg-emerald-600/30 px-1.5 py-0.5 rounded-md font-mono">
              ⌘K
            </span>
          </button>

          {/* Data Entry Button - Only show if onToggleAutomaticEntry is provided */}
          {onToggleAutomaticEntry && (
            <motion.button
              onClick={onToggleAutomaticEntry}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="group flex items-center justify-center gap-2 px-3.5 py-2 rounded-xl
                font-medium text-sm
                bg-gradient-to-r from-sky-500 via-sky-500 to-sky-400
                text-white shadow-lg hover:shadow-xl hover:brightness-110
                transition-all duration-200
                w-full"
            >
              <PlusCircle size={18} className="text-white" />
              <span>Data Entry</span>
              <span className="text-xs text-sky-100 bg-sky-600/30 px-1.5 py-0.5 rounded-md font-mono">
                ⌘E
              </span>
            </motion.button>
          )}

          {/* Position Indicator */}
          <div className="flex items-center justify-center px-3 py-1.5 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-400">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span>Row {(activeCell && activeCell[0] !== null) ? activeCell[0] + 1 : '-'}</span>
              <div className="w-1 h-1 bg-slate-400 rounded-full"></div>
              <span>Col {(activeCell && activeCell[1] !== null) ? activeCell[1] + 1 : '-'}</span>
              {/* Show selection size in the same div if applicable */}
              {selectionStart && selectionEnd && (
                selectionStart[0] !== selectionEnd[0] || selectionStart[1] !== selectionEnd[1]
              ) && (
                <>
                  <div className="w-px h-4 bg-slate-300 mx-2"></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                  <span className="text-indigo-600">
                    {Math.abs(selectionEnd[0] - selectionStart[0]) + 1} × {Math.abs(selectionEnd[1] - selectionStart[1]) + 1}
                  </span>
                </>
              )}
              {/* Show editing indicator in the same div if applicable */}
              {editingCell && (
                <>
                  <div className="w-px h-4 bg-slate-300 mx-2"></div>
                  <Edit size={14} className="text-green-500" />
                  <span className="text-green-600">Editing</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <motion.div
        initial={false}
        animate={{
          height: isButtonContainerExpanded ? 'auto' : 0,
          opacity: isButtonContainerExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ overflow: 'hidden' }}
      >
        <div className="p-4 space-y-4 pt-0">
          {/* Action buttons group */}
          <div className="flex flex-wrap gap-3">
            {buttonConfigs.map((button, index) => (
              <motion.button
                key={index}
                onClick={button.onClick}
                disabled={button.disabled}
                whileHover={{ scale: button.disabled ? 1 : 1.02, y: button.disabled ? 0 : -1 }}
                whileTap={{ scale: button.disabled ? 1 : 0.98 }}
                className={`
                  relative group flex items-center justify-center gap-2.5
                  px-3.5 py-2 
                  rounded-xl
                  font-medium text-sm
                  transition-all duration-300
                  flex-1 min-w-0
                  ${button.primary 
                    ? `bg-gradient-to-r ${button.gradient || 'from-slate-600 to-slate-700'} 
                       text-white shadow-lg hover:shadow-xl hover:brightness-110
                       transition-all duration-200`
                    : `bg-sky-200 backdrop-blur-sm text-sky-900 
                       hover:bg-sky-300 hover:text-sky-950 hover:border-sky-500
                       border border-sky-400 
                       shadow-sm hover:shadow-md`
                  }
                  ${button.disabled
                    ? 'opacity-40 cursor-not-allowed'
                    : ''
                  }
                `}
              >
                <button.icon 
                  size={18} 
                  className={`relative z-10 ${button.primary ? 'text-white' : 'text-sky-800 group-hover:text-sky-900'}`}
                />
                <span className="relative z-10">{button.text}</span>
                {button.shortcut && !button.primary && (
                  <span className="hidden sm:inline-block text-xs text-slate-400 bg-slate-100 
                    px-1.5 py-0.5 rounded-md font-mono relative z-10">
                    {button.shortcut}
                  </span>
                )}
              </motion.button>
            ))}
          </div>

          {/* Filter Controls Section */}
          <div className="space-y-3">            
            <div className="flex flex-col sm:flex-row gap-3 items-stretch">
              {/* Column Select Filter */}
              <div className="flex-1" ref={columnSelectRef}>
                <Select
                  isMulti
                  value={selectedColumns.map(column => ({
                    value: column,
                    label: visibleColumnNames[column]
                  }))}
                  options={Object.keys(visibleColumnNames)
                    .filter(key => key !== 'Company Name') // Remove Company Name from options
                    .map(key => ({
                      value: key,
                      label: visibleColumnNames[key]
                    }))}
                  onChange={(selected) => {
                    const newSelection = selected ? selected.map(option => option.value) : [];
                    setSelectedColumns(newSelection);
                  }}
                  placeholder="Filter columns (showing all)..."
                  className="column-filter"
                  classNamePrefix="react-select"
                  isSearchable={true}
                  menuPortalTarget={document.body}
                  menuPlacement="auto"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '44px',
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderColor: state.isFocused ? '#0ea5e9' : '#0ea5e9',
                      borderWidth: '2px',
                      boxShadow: state.isFocused ? '0 0 0 3px rgba(14, 165, 233, 0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
                      borderRadius: '1rem',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#0284c7',
                        boxShadow: '0 4px 16px rgba(14, 165, 233, 0.15)',
                      },
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '2px 8px',
                      flexWrap: 'nowrap',
                      overflow: 'hidden',
                      maxHeight: '40px',
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(219, 234, 254, 0.7)',
                      borderRadius: '0.5rem',
                      margin: '2px',
                      border: '1px solid rgba(147, 197, 253, 0.5)',
                      maxWidth: '100px',
                      flexShrink: 0,
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      fontSize: '0.875rem',
                      padding: '2px 6px',
                      color: '#1e40af',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '80px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#3b82f6',
                      ':hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                      },
                      borderRadius: '0 0.5rem 0.5rem 0',
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: 'rgba(255, 255, 255, 0.98)',
                      borderRadius: '0.75rem',
                      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      overflow: 'hidden',
                      zIndex: 9999,
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(226, 232, 240, 0.8)',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected 
                        ? 'rgba(59, 130, 246, 0.9)' 
                        : isFocused 
                          ? 'rgba(59, 130, 246, 0.1)' 
                          : 'transparent',
                      color: isSelected ? 'white' : '#334155',
                      ':active': {
                        backgroundColor: 'rgba(59, 130, 246, 0.8)',
                        color: 'white',
                      },
                      padding: '10px 14px',
                      fontSize: '0.875rem',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#64748b',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                    }),
                    input: (base) => ({
                      ...base,
                      fontSize: '0.875rem',
                      color: '#334155',
                      width: '0px',
                      minWidth: '0px',
                      flex: '0 0 auto',
                      margin: '0px',
                      padding: '0px',
                    }),
                    inputContainer: (base) => ({
                      ...base,
                      margin: '0px',
                      padding: '0px',
                      '&:after': {
                        display: 'none',
                      },
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      padding: '4px',
                      color: '#94a3b8',
                      ':hover': {
                        color: '#475569',
                      },
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: '4px',
                      color: '#94a3b8',
                      ':hover': {
                        color: '#475569',
                      },
                    }),
                  }}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  filterOption={(option, inputValue) => {
                    const label = option.label.toLowerCase();
                    const input = inputValue.toLowerCase();
                    
                    if (label === input) return true;
                    if (label.startsWith(input)) return true;
                    if (label.includes(` ${input}`)) return true;
                    if (label.includes(input)) return true;
                    
                    return false;
                  }}
                  noOptionsMessage={({ inputValue }) => 
                    inputValue ? "No matching columns" : "Type to search columns"
                  }
                />
              </div>

              {/* Global search input */}
              <div className="flex-1 relative group flex items-center">
                <div className="w-full h-full relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none z-10">
                    <SearchIcon size={18} className="text-sky-500 group-focus-within:text-sky-600 transition-colors" />
                  </div>
                  <input
                    type="text"
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    placeholder={`Search ${currentSheetType}...`}
                    className="w-full h-full min-h-[44px] pl-10 pr-4 bg-white/95 rounded-2xl 
                      shadow-md hover:shadow-lg focus:shadow-xl
                      border-2 border-sky-500 hover:border-sky-600 focus:border-sky-600
                      focus:outline-none focus:ring-3 focus:ring-sky-500/20
                      transition-all duration-200 text-sm text-slate-700 font-medium
                      placeholder:text-slate-600 placeholder:font-medium"
                  />
                  {globalSearch && (
                    <button
                      onClick={() => setGlobalSearch('')}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 
                        hover:text-slate-600 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default ActionButtons;